import { CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { cx } from "../../../utils/cx";
import { formatKigaliDate, kindOf } from "../model";

// Each kind's path after "Started"; the student's status marks where they are on it
// A sponsored scholarship is granted once a university admits the student; until then it is with the sponsor
const SPONSORED = ["SUBMITTED", "UNDER_EVALUATION", "WITH_DG", "WITH_SPONSOR", "GRANTED"];
const LOAN = ["SUBMITTED", "UNDER_EVALUATION", "WITH_DG", "WITH_MINISTER", "APPROVED", "LOAN_ACCEPTED", "TRAVEL_DETAILS_SENT", "TICKET_ISSUED", "TUITION_PAID"];

const LABELS = {
  SUBMITTED: "Submitted",
  UNDER_EVALUATION: "Under evaluation",
  WITH_DG: "With the DG",
  WITH_SPONSOR: "With the partner",
  GRANTED: "Granted",
  WITH_MINISTER: "With the Minister",
  APPROVED: "Approved",
  LOAN_ACCEPTED: "Loan accepted",
  TRAVEL_DETAILS_SENT: "Travel details",
  TICKET_ISSUED: "Ticket",
  TUITION_PAID: "Paid by BRD",
};

// Someone else is working on these: the step stays current until it moves on
const IN_PROGRESS = new Set(["UNDER_EVALUATION", "WITH_DG", "WITH_SPONSOR", "WITH_MINISTER"]);

// Statuses that sit on another step of the path: [step, label, tone]; a stopped one ends the path there
const ON_STEP = {
  DRAFT: ["SUBMITTED", "Submit", "current"],
  RESERVE_LIST: ["WITH_DG", "Reserve list", "current"],
  NOT_SELECTED: ["WITH_DG", "Not selected", "stopped"],
  RETURNED_TO_HEC: ["WITH_DG", "Returned to HEC", "current"],
  NOT_GRANTED: ["GRANTED", "Not granted", "stopped"],
  REJECTED: ["APPROVED", "Rejected", "stopped"],
  LOAN_DECLINED: ["LOAN_ACCEPTED", "Loan declined", "stopped"],
  ALLOWANCE_SENT: ["TUITION_PAID", "Paid by BRD", "done"],
};

// When each step happened, where the application says so
const dateOf = (step, { submittedAt, outcomePublishedAt, loan }) =>
  ({
    SUBMITTED: submittedAt,
    GRANTED: outcomePublishedAt,
    APPROVED: outcomePublishedAt,
    LOAN_ACCEPTED: loan?.response?.at,
    TRAVEL_DETAILS_SENT: loan?.travel?.submittedAt,
    TICKET_ISSUED: loan?.travel?.ticket?.issuedAt,
  })[step] ?? null;

/**
 * The application's whole path, for its kind (a sponsored scholarship, or a loan), with every step
 * done (with its date where known), current or still to come. The student's status is shown exactly
 * where it is; a refusal, a withdrawal or a missed deadline ends the path at that step.
 */
const stepsOf = (application) => {
  const { studentStatus: status, createdAt, submittedAt, scholarship } = application;
  const started = { label: "Started", date: createdAt, tone: "done" };
  const submitted = scholarship.type === "COOPERATION" ? "Received by HEC" : "Submitted";

  if (status === "NOT_SUBMITTED") return [started, { label: "Not submitted", date: scholarship.closesAt, note: "Deadline passed", tone: "stopped" }];
  if (status === "WITHDRAWN") {
    const steps = [started];
    if (submittedAt) steps.push({ label: submitted, date: submittedAt, tone: "done" });
    return [...steps, { label: "Withdrawn", tone: "stopped" }];
  }

  const kind = kindOf(scholarship.type);
  const path = kind.isLoan ? LOAN : SPONSORED;
  const [step, label, tone] = ON_STEP[status] ?? [status, LABELS[status], IN_PROGRESS.has(status) ? "current" : "done"];
  const at = Math.max(0, path.indexOf(step));
  const closed = new Date(scholarship.closesAt) <= new Date();
  const steps = path.map((key, i) => {
    const name = key === "SUBMITTED" ? submitted : LABELS[key];
    if (i < at) return { label: name, date: dateOf(key, application), tone: "done" };
    if (i === at) return { label, date: tone === "current" ? null : dateOf(key, application), tone };
    // The step after a done one is the one now waiting
    const next = i === at + 1 && tone === "done";
    const note = key === "UNDER_EVALUATION" && !closed ? `Starts after ${formatKigaliDate(scholarship.closesAt)}` : null;
    return { label: name, note, tone: next && closed ? "current" : "upcoming" };
  });
  return [started, ...(tone === "stopped" ? steps.slice(0, at + 1) : steps)];
};

const DOT = {
  done: "bg-brand text-white",
  current: "border border-brand bg-white text-brand",
  upcoming: "border border-slate-200 bg-white text-slate-400",
  stopped: "bg-red-600 text-white",
};

export default function StatusTracker({ application }) {
  const steps = stepsOf(application);
  return (
    <ol className="flex flex-col gap-3 sm:flex-row sm:gap-0">
      {steps.map((step, i) => (
        <li key={step.label} className="relative flex items-center gap-3 sm:flex-1 sm:flex-col sm:text-center">
          {/* The line to the next step, filled once this step is done */}
          {i < steps.length - 1 && (
            <span
              aria-hidden
              className={cx(
                "absolute left-[15px] top-[30px] h-[calc(100%-1.125rem)] w-px sm:left-[calc(50%+15px)] sm:top-[15px] sm:h-px sm:w-[calc(100%-30px)]",
                step.tone === "done" ? "bg-brand" : "bg-slate-200"
              )}
            />
          )}
          <span className={cx("relative z-10 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-sm font-semibold", DOT[step.tone])}>
            {step.tone === "done" ? <CheckIcon className="h-[18px] w-[18px]" /> : step.tone === "stopped" ? <XMarkIcon className="h-[18px] w-[18px]" /> : i + 1}
          </span>
          <div className="flex flex-wrap items-baseline gap-x-2 sm:mt-1.5 sm:flex-col sm:items-center">
            <p className={cx("text-sm font-semibold", step.tone === "upcoming" ? "text-slate-400" : step.tone === "stopped" ? "text-red-700" : "text-slate-800")}>
              {step.label}
              {step.tone === "current" && <span className="ml-1.5 rounded bg-brand/10 px-1.5 text-xs font-medium text-brand">Now</span>}
            </p>
            {step.date && <p className="text-xs text-slate-500">{formatKigaliDate(step.date)}</p>}
            {step.note && <p className="text-xs text-slate-500">{step.note}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}
